import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Calendar, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { seoData, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';

// Golden Glow Line Component
const GoldenGlowLine = () => (
  <div className="relative h-[2px] bg-black overflow-hidden">
    <div 
      className="absolute top-0 h-full w-[300px]"
      style={{
        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
        animation: 'glowLine 4s ease-in-out infinite',
        boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
      }}
    />
  </div>
);

// Animated Corner Frame Component (animation in index.css)
const AnimatedCornerFrame = ({ children, className = "" }) => (
  <div className={`group relative ${className}`}>
    {/* Top-left corner frame */}
    <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-2xl pointer-events-none">
      <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
        <div 
          className="absolute left-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVertical 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
        <div 
          className="absolute top-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    
    {/* Bottom-right corner frame */}
    <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-2xl pointer-events-none">
      <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
        <div 
          className="absolute right-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
        <div 
          className="absolute bottom-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    {children}
  </div>
);

const NewsPage = () => {
  const { t } = useLanguage();

  const newsItems = [
    { 
      id: 1, 
      title: { 
        de: 'Erweiterung unserer Housekeeping-Services',
        en: 'Expansion of Our Housekeeping Services'
      },
      excerpt: { 
        de: 'Ab sofort bieten wir erweiterte Housekeeping-Dienstleistungen für Hotels in Brandenburg an.',
        en: 'We now offer expanded housekeeping services for hotels in Brandenburg.'
      },
      date: { de: '15. Juni 2025', en: 'June 15, 2025' },
      category: { de: 'Unternehmen', en: 'Company' },
      image: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&h=400&fit=crop'
    },
    { 
      id: 2, 
      title: { 
        de: 'Neue Partnerschaft mit Alba Berlin',
        en: 'New Partnership with Alba Berlin'
      },
      excerpt: { 
        de: 'Wir sind stolz, offizieller Sponsoring-Partner von Alba Berlin zu sein.',
        en: 'We are proud to be an official sponsoring partner of Alba Berlin.'
      },
      date: { de: '1. Mai 2025', en: 'May 1, 2025' },
      category: { de: 'Partnerschaft', en: 'Partnership' },
      image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600&h=400&fit=crop'
    },
    { 
      id: 3, 
      title: { 
        de: 'Team-Erweiterung im Front Office Bereich',
        en: 'Team Expansion in Front Office'
      },
      excerpt: { 
        de: 'Unser Front Office Team wächst weiter, um noch mehr Hotels in Berlin zu unterstützen.',
        en: 'Our front office team continues to grow to support even more hotels in Berlin.'
      },
      date: { de: '20. April 2025', en: 'April 20, 2025' },
      category: { de: 'Team', en: 'Team' },
      image: 'https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?w=600&h=400&fit=crop'
    }
  ];
  
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.news.title}
        description={seoData.news.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">
                  {t('current', { de: 'Aktuelles', en: 'Latest' })}
                </span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">
                {t('title', translations.news)}
              </h1>
              <p className="text-xl text-black/60 max-w-2xl mx-auto">
                {t('subtitle', translations.news)}
              </p>
            </div>
          </div>
        </section>
        <GoldenGlowLine />
        <section className="py-20 bg-[#F9F9F9]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newsItems.map((item) => (
                <AnimatedCornerFrame key={item.id} className="rounded-2xl overflow-hidden bg-white border border-black/5 hover:shadow-xl transition-all duration-500">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={item.image} alt={t('title', item.title)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-[#D4AF37] text-[#003366] text-xs font-medium">
                        {t('category', item.category)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-black/40 text-sm mb-3">
                      <Calendar size={14} />
                      <span>{t('date', item.date)}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-black mb-3 group-hover:text-[#D4AF37] transition-colors">
                      {t('title', item.title)}
                    </h3>
                    <p className="text-black/60 text-sm leading-relaxed mb-4">
                      {t('excerpt', item.excerpt)}
                    </p>
                    <button className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium group-hover:gap-3 transition-all">
                      <span>{t('readMore', translations.news)}</span>
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </AnimatedCornerFrame>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NewsPage;
